import { WBFlex, WBIcon, WBTypography } from '@admiin-com/ds-web';
import { useMediaQuery, useTheme } from '@mui/material';
import { Annotation, AnnotationsUnion, Color } from '@nutrient-sdk/viewer';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import ConfirmationDlg from '../ConfirmationDlg/ConfirmationDlg';
import {
  createCustomSignatureNode,
  getSignatureColor,
} from '../../helpers/signature';
import { PdfPlaceholderV2 } from '../PdfPlaceholder/PdfPlaceholder';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';
import { useUserSignature } from '../../hooks/useUserSignature/useUserSignature';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCurrentUser } from '../../hooks/useCurrentUser/useCurrentUser';
import { createRoot } from 'react-dom/client';
import { stripPdfIdFromAnnotations } from '../../helpers/tasks';
import { getNutrientConfig } from '../../features/hooks/useNutrientPreload/useNutrientPreload';

export interface SignatureDropAreaProps {
  documentUrl: string;
  annotations?: any;
  userId?: string;
  onPdfLoad?: (totalPageCount: number) => void;
  onDrop?: (signer: any) => void;
  onAnnotationUpdated?: () => void;
}

interface DeleteAnnotation {
  type: string; // to show different title for different annotation type
  id: string;
}

const renderConfigurations: Record<any, any> = {};

export const SignatureDropArea = forwardRef(
  (
    {
      documentUrl,
      onPdfLoad,
      annotations,
      onAnnotationUpdated,
      userId,
      onDrop,
    }: SignatureDropAreaProps,
    instanceRef: any
  ) => {
    const { t } = useTranslation();
    //TODO: valid ref type
    const NutrientViewer = useRef<any>(null); //TODO: types
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const containerRef = useRef(null);
    //let isDragAndDropSupported = false; //TODO: purpose?
    const [showDeleteDlg, setShowDeleteDlg] =
      useState<DeleteAnnotation | null>();
    const [showAddSignModal, setShowAddSignModal] = useState<any>(null);
    const { userSignatureKey, getSignatureBlob } = useUserSignature();

    //TODO: this is rendering each movement / event, should it be so?

    const isLoggedInUser = (signerUserId: string) => {
      return userId === signerUserId;
    };

    const { control } = useFormContext();
    const signers = useWatch({ control, name: 'signers' });
    const user = useCurrentUser();
    const validSigners = React.useMemo(
      () =>
        [
          {
            id: user.id,
            isUser: true,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            mobile: user.phone ?? '',
          },
        ].concat(signers.filter((signer: any) => signer.name && signer.email)),
      [signers, user]
    );

    const signersRef = useRef(validSigners);

    React.useEffect(() => {
      signersRef.current = validSigners;
    }, [validSigners]);

    useEffect(() => {
      const container = containerRef.current; // render Nutrient Viewer ui to this container
      const getAnnotationRenderers = ({
        annotation,
      }: {
        annotation: AnnotationsUnion;
      }) => {
        // Use cached render configuration
        if (renderConfigurations[annotation.id]) {
          return renderConfigurations[annotation.id];
        }
        const { customData } = annotation;
        const placeholderUi = (
          <PdfPlaceholderV2 customData={{ ...customData }} />
        );
        const customNode = createCustomSignatureNode(placeholderUi, customData);
        renderConfigurations[annotation.id] = {
          node: customNode,
          append: true,
        };

        return renderConfigurations[annotation.id] || null;
      };

      const annotationTooltipCallback = (annotation: Annotation) => {
        const tooltipNode = document.createElement('div');

        const iconContainer = document.createElement('div');
        const iconRoot = createRoot(iconContainer);
        iconRoot.render(
          // @ts-ignore - TypeScript issue with ReactNode type
          <WBIcon name="CloseCircle" color={theme.palette.error.main} />
        );

        tooltipNode.style.cssText = `
        position: absolute;
        top: -20px; /* Adjust to position above the annotation */
        left: 50%;
        transform: translateX(-50%);
      `;
        //@ts-ignore TODO: resolve type issue
        // tooltipNode.innerHTML = renderToString(deleteIcon);

        tooltipNode.appendChild(iconContainer);
        const customTooltipItem = {
          type: 'custom',
          node: tooltipNode,
          onPress: function () {
            setShowDeleteDlg({
              type: 'Signature',
              id: annotation.id,
            });
          },
        };

        const signerNode = document.createElement('div');
        signerNode.style.cssText = `
           position: absolute;
          top: -35px; /* Adjust to position above the annotation */
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
          display:flex;
          flex-direction:row;
        `;
        signerNode.innerHTML = `<div></div>`;

        const data: any = annotation.customData;
        //@ts-ignore TODO: resolve type issue
        for (let i = 0; i < signersRef.current.length; i++) {
          const colorPicker = document.createElement('div') as HTMLElement;
          colorPicker.style.cssText = `
            width: 15px;
            height: 15px;
            border-radius: 100%;
            background-color: ${
              signersRef.current[i].isUser
                ? '#ffffff'
                : getSignatureColor(i - 1)
            };
            margin-left: ${i > 0 ? '5px' : '0'};
            border: 1px solid ${theme.palette.primary.main};
            `;
          colorPicker.addEventListener('click', () => {
            changeAnnotation(annotation, i);
          });
          //@ts-ignore TODO: resolve type issue
          signerNode.append(colorPicker);
        }

        const signerNodeItem = {
          type: 'custom',
          node: signerNode,
          onPress: function () {
            console.log('color picker clicked');
          },
        };

        const colorChange =
          data && data.type !== 'SIGNATURE' && signersRef.current.length > 1;
        if (colorChange) return [customTooltipItem, signerNodeItem];
        return [customTooltipItem];
      };

      const changeAnnotation = (annotation: Annotation, i: number) => {
        let label;
        if (annotation.customData?.type === 'NAME') {
          label = signersRef.current[i]?.name ?? annotation.customData?.name;
        } else if (annotation.customData?.type === 'INITIAL') {
          const name =
            signersRef.current[i]?.name ?? annotation.customData?.name ?? '';
          label = name
            ? name
                .split(' ')
                .filter((n) => n)
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
            : '';
        } else if (
          annotation.customData?.type === 'DATE' &&
          signersRef.current[i]?.isUser
        ) {
          label = new Date().toLocaleDateString();
        } else {
          label = 'Date';
        }
        const updatedAnnotation =
          new NutrientViewer.current.Annotations.TextAnnotation({
            pageIndex: annotation.pageIndex,
            boundingBox: annotation.boundingBox,
            id: uuidv4(),
            lockedContents: true,
            fontSize: 12,
            backgroundColor: Color.TRANSPARENT,
            text: {
              format: 'plain',

              value: label,
            },
            horizontalAlign: 'center',
            verticalAlign: 'center',
            customData: {
              ...annotation.customData,
              color: getSignatureColor(i - 1),
              signerId:
                signersRef.current[i]?.id ?? annotation.customData?.signerId,
              name: signersRef.current[i]?.name ?? annotation.customData?.name,
              label: label,
              email:
                i === 0
                  ? annotation.customData?.email
                  : signersRef.current[i]?.email ??
                    annotation.customData?.email,
              mobile:
                signersRef.current[i]?.mobile ?? annotation.customData?.mobile,
              status: signersRef.current[i]?.isUser ? 'ACTIONED' : 'PENDING',
            },
          });
        console.log(updatedAnnotation.customData);

        instanceRef.current.delete(annotation).then(() => {
          instanceRef.current.create(updatedAnnotation).then(() => {
            instanceRef.current.setSelectedAnnotation(updatedAnnotation.id);
          });
        });
      };

      (async function () {
        NutrientViewer.current = await import('@nutrient-sdk/viewer'); // Load Nutrient Viewer asynchronously.
        NutrientViewer.current.unload(container); // unload any existing instances

        const input: any = {
          container,
          document: documentUrl,
          autoSaveMode: 'INTELLIGENT', //TODO: may not require
          instantJSON: undefined,
          ...getNutrientConfig(),
          //toolbarItems: [],
          //toolbarItems,
          enableHistory: true,
          disableTextSelection: false,
          customRenderers: {
            Annotation: getAnnotationRenderers,
          },
          initialViewState: isMobile
            ? new NutrientViewer.current.ViewState({
                showToolbar: false,
              })
            : new NutrientViewer.current.ViewState({
                showToolbar: false,
                sidebarMode: NutrientViewer.current.SidebarMode.THUMBNAILS,
                sidebarPlacement: NutrientViewer.current.SidebarPlacement.END,
              }),
          annotationTooltipCallback,
          styleSheets: ['/viewer.css'], //TODO: review
          inlineTextSelectionToolbarItems: () => {
            const highlightButton = {
              type: 'custom',
              id: 'highlight-text',
              title: t('highlightText', { ns: 'taskbox' }),
              onPress: async () => {
                try {
                  const textSelection = instanceRef.current.getTextSelection();
                  if (textSelection) {
                    // Group text lines by pageIndex within the selection range
                    const pageLineMap: { [key: number]: any[] } = {};
                    textSelection.textLines
                      .filter(
                        (line: any) =>
                          line.pageIndex >= textSelection.startPageIndex &&
                          line.pageIndex <= textSelection.endPageIndex
                      )
                      .forEach((line: any) => {
                        if (!pageLineMap[line.pageIndex]) {
                          pageLineMap[line.pageIndex] = [];
                        }
                        pageLineMap[line.pageIndex].push(line.boundingBox);
                      });

                    // Create a highlight annotation for each page
                    for (const [pageIndex, rects] of Object.entries(
                      pageLineMap
                    )) {
                      const highlightAnnotation =
                        new NutrientViewer.current.Annotations.HighlightAnnotation(
                          {
                            pageIndex: Number(pageIndex),
                            rects: rects,
                            color: new NutrientViewer.current.Color({
                              r: 255,
                              g: 234,
                              b: 0,
                            }), // Yellow highlight color
                            id: uuidv4(),
                          }
                        );
                      await instanceRef.current.create(highlightAnnotation);
                    }
                    if (onAnnotationUpdated) {
                      onAnnotationUpdated();
                    }
                  }
                } catch (e) {
                  console.error('Error creating highlight annotation:', e);
                }
              },
              node: (() => {
                const button = document.createElement('button');
                button.style.cssText = `
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 8px;
                  background: #FFEA00;
                  border: none;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                `;
                button.innerHTML = `
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 14l3 3 7-7-3-3-7 7zm13.71-9.37l-1.34-1.34a.996.996 0 00-1.41 0L15 5.25 18.75 9l1.96-1.96c.39-.39.39-1.02 0-1.41z" fill="currentColor"/>
                    <path d="M2 20h20v4H2z" fill="#FFEA00"/>
                  </svg>
                `;
                button.title = t('highlightText', { ns: 'taskbox' });
                return button;
              })(),
            };
            return [highlightButton];
          },
        };
        if (annotations) {
          const strippedAnnotations = stripPdfIdFromAnnotations(annotations);
          if (strippedAnnotations) {
            input.instantJSON = JSON.parse(strippedAnnotations);
          }
        }

        // load new instance
        instanceRef.current = await NutrientViewer.current.load(input);

        // Remove any existing PDF form fields (and their widget annotations)
        // to avoid Nutrient Viewer default external form elements interfering with
        // our custom e-signature placeholders/UI.
        try {
          const formFields = await instanceRef.current.getFormFields();
          const fields: any[] = [];

          // Filter to collect only NON-signature form fields
          formFields?.forEach((field: any) => {
            // Check if the field is NOT a signature field
            // Nutrient Viewer signature fields have specific identifying properties
            const isSignatureField =
              field.name?.toLowerCase().includes('signature') ||
              field.type === 'signature' ||
              field.fieldType === 'Sig';

            if (isSignatureField) {
              fields.push(field);
            }
          });

          if (fields.length > 0) {
            await Promise.all(
              fields.map((field: any) => instanceRef.current.delete(field))
            );
          }
        } catch (e) {
          console.log('Failed to remove non-signature PDF form fields', e);
        }

        if (instanceRef?.current) {
          instanceRef.current.handleDrop = handleDrop;
        }
        instanceRef.current?.addEventListener(
          'annotations.willChange',
          (event: any) => {
            console.log('The user is drawing...');
            console.log(event);
            const annotations = event.annotations;
            instanceRef.current.update(annotations.get(0));
            onAnnotationUpdated && onAnnotationUpdated();
          }
        );

        onPdfLoad && onPdfLoad(instanceRef.current.totalPageCount);
      })();
      return () => NutrientViewer.current?.unload(container);
    }, [annotations, documentUrl, instanceRef, theme, isMobile]);

    const handleDrop = useCallback(
      (event: any, clickEvent?: any) => {
        //TODO: type
        if (!NutrientViewer.current || !instanceRef.current) return;
        event.preventDefault();
        event.stopPropagation();

        const createPlaceholderAnnotation = (
          annotationObj: any,
          label: string
        ) => {
          return new NutrientViewer.current.Annotations.TextAnnotation({
            ...annotationObj,
            text: {
              format: 'plain',
              background: annotationObj.color,
              value: label,
            },
            opacity: 0,
          });
        };
        (async function () {
          const color =
            event?.dataTransfer?.getData('color') ?? clickEvent?.color;
          const email =
            event?.dataTransfer?.getData('email') ?? clickEvent?.email;
          const label =
            event?.dataTransfer?.getData('label') ?? clickEvent?.label;
          const type = event?.dataTransfer?.getData('type') ?? clickEvent?.type;
          const signerId =
            event?.dataTransfer?.getData('signerId') ?? clickEvent?.signerId;
          const signerEntityId =
            event?.dataTransfer?.getData('entityId') ?? clickEvent?.entityId;

          const signerType =
            event?.dataTransfer?.getData('signerType') ??
            clickEvent?.signerType;
          const name = event?.dataTransfer?.getData('name') ?? clickEvent?.name;
          const signatureKey =
            event?.dataTransfer?.getData('signatureKey') ??
            clickEvent?.signatureKey;
          const mobile =
            event?.dataTransfer?.getData('mobile') ?? clickEvent?.mobile;

          if (onDrop && type === 'SIGNATURE') {
            onDrop({
              color: color,
              label,
              type: type,
              id: signerId,
              email,
              entityId: signerEntityId,
              signerType: signerType,
              name,
              signatureKey: signatureKey,
              mobile,
            });
          }

          let pageIndex = instanceRef.current.viewState.currentPageIndex;
          const isRectInsidePage = (pageIndex: number, rect: any) => {
            const pageInfo = instanceRef.current.pageInfoForIndex(pageIndex);
            const pageRectSize = new NutrientViewer.current.Geometry.Rect({
              left: 0,
              top: 0,
              width: pageInfo.width,
              height: pageInfo.height,
            });

            return pageRectSize.isRectOverlapping(rect);
          };
          const getPageRect = async (pageIndex: number) => {
            const height =
              type === 'SIGNATURE' ? 55 : type === 'INITIAL' ? 20 : 15;
            const width =
              type === 'SIGNATURE' || type === 'NAME'
                ? 150
                : type === 'INITIAL'
                ? 30
                : 80;

            const left = clickEvent
              ? window.innerWidth / 2 - (isMobile ? width / 2 : 0)
              : event.clientX - width / 2;
            const top = clickEvent
              ? window.innerHeight / 2 - height / 2 - (isMobile ? 90 : 0)
              : event.clientY - height / 2;
            const clientRect = new NutrientViewer.current.Geometry.Rect({
              left,
              top,
              height,
              width,
            });

            const pageRect =
              await instanceRef.current.transformContentClientToPageSpace(
                clientRect,
                pageIndex
              );
            if (!isRectInsidePage(pageIndex, pageRect)) {
              throw new Error('page rect is not inside page');
            }
            return pageRect;
          };
          let pageRect;
          try {
            pageRect = await getPageRect(pageIndex);
          } catch {
            try {
              console.log('try to get page rect from next page');
              pageIndex = instanceRef.current.viewState.currentPageIndex - 1;
              pageRect = await getPageRect(pageIndex);
            } catch {
              console.log('try to get page rect from previous page');
              pageIndex = instanceRef.current.viewState.currentPageIndex + 1;
              try {
                pageRect = await getPageRect(pageIndex);
              } catch {
                throw new Error('page rect is not inside page');
              }
            }
          }

          const id = uuidv4();
          const annotationObj = {
            pageIndex,
            boundingBox: pageRect,
            id: id,
            lockedContents: true,
            backgroundColor: Color.TRANSPARENT,
            fontSize: 12,
            selected: true,
            customData: {
              type,
              color,
              name,
              email,
              signerId,
              label,
              status: isLoggedInUser(signerId) ? 'ACTIONED' : 'PENDING',
              signerType,
              mobile,
            },
          };
          let annotation;
          if (isLoggedInUser(signerId)) {
            if (type === 'SIGNATURE') {
              if (signatureKey) {
                let signatureBlob;
                try {
                  signatureBlob = await getSignatureBlob(signatureKey);
                  console.log('signatureBlob: ', signatureBlob);
                  console.log('type of signatureBlob: ', typeof signatureBlob);
                } catch {
                  console.log('error creating image attachment');
                }
                try {
                  const attachmentId =
                    await instanceRef.current.createAttachment(signatureBlob);
                  console.log('attachmentId: ', attachmentId);
                  annotation =
                    new NutrientViewer.current.Annotations.ImageAnnotation({
                      ...annotationObj,
                      contentType: 'image/jpeg',
                      isSignature: true,
                      imageAttachmentId: attachmentId,
                    });
                } catch {
                  console.log('error creating image annotation');
                }
              } else {
                setShowAddSignModal(annotationObj);
                return;
              }
            } else if (type === 'NAME') {
              annotation =
                new NutrientViewer.current.Annotations.TextAnnotation({
                  ...annotationObj,
                  text: {
                    background: color,
                    format: 'plain',
                    fontSize: 12,
                    value: name,
                  },
                  horizontalAlign: 'left',
                  verticalAlign: 'center',
                });
            } else if (type === 'DATE') {
              annotation =
                new NutrientViewer.current.Annotations.TextAnnotation({
                  ...annotationObj,
                  text: {
                    background: color,
                    format: 'plain',
                    fontSize: 12,
                    value: new Date().toLocaleDateString(),
                  },
                  horizontalAlign: 'left',
                  verticalAlign: 'center',
                });
            } else if (type === 'TEXT') {
              annotation =
                new NutrientViewer.current.Annotations.TextAnnotation({
                  ...annotationObj,
                  lockedContents: false,
                  text: {
                    background: color,
                    format: 'plain',
                    fontSize: 12,
                    value: '',
                  },
                  horizontalAlign: 'left',
                  verticalAlign: 'center',
                });
            } else if (type === 'INITIAL') {
              // Generate initials from the signer's name
              const initials = name
                ? name
                    .split(' ')
                    .filter((n: string) => n)
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()
                : '';
              annotation =
                new NutrientViewer.current.Annotations.TextAnnotation({
                  ...annotationObj,
                  text: {
                    background: color,
                    format: 'plain',
                    fontSize: 12,
                    value: initials,
                  },
                  horizontalAlign: 'center',
                  verticalAlign: 'center',
                });
            }
          } else {
            annotation = createPlaceholderAnnotation(annotationObj, label);
          }
          try {
            // Check if the annotation's bounding box is inside the page's bounding box

            await instanceRef.current.create(annotation);
            if (type === 'TEXT') {
              await instanceRef.current.setEditingAnnotation(annotation);
            } else {
              await instanceRef.current.setSelectedAnnotation(annotation.id);
            }
          } catch {
            console.log('error creating/setting annotation');
          }

          if (onAnnotationUpdated) {
            onAnnotationUpdated();
          }
        })();
      },
      [instanceRef, userSignatureKey]
    );

    const addSignatureToPdf = async (
      signatureKey: string,
      annotationObj: any
    ) => {
      const signatureBlob = await getSignatureBlob(signatureKey);
      const attachmentId = await instanceRef.current.createAttachment(
        signatureBlob
      );
      const annotation = new NutrientViewer.current.Annotations.ImageAnnotation(
        {
          ...annotationObj,
          contentType: 'image/jpeg',
          imageAttachmentId: attachmentId,
        }
      );
      instanceRef.current.create(annotation);
    };

    const addInitialsToAllPages = useCallback(
      async (signer: any, color: string) => {
        if (!NutrientViewer.current || !instanceRef.current) return;

        const totalPageCount = instanceRef.current.totalPageCount;
        const name = signer.name ?? '';
        const initials = name
          .split(' ')
          .filter((n: string) => n)
          .map((n: string) => n[0])
          .join('')
          .toUpperCase();

        for (let pageIndex = 0; pageIndex < totalPageCount; pageIndex++) {
          const pageInfo = instanceRef.current.pageInfoForIndex(pageIndex);
          const margin = 20;
          const width = 40;
          const height = 20;

          const boundingBox = new NutrientViewer.current.Geometry.Rect({
            left: pageInfo.width - width - margin,
            top: pageInfo.height - height - margin,
            width,
            height,
          });

          const id = uuidv4();
          const isCurrentUser = userId === signer.id;
          const annotationObj = {
            pageIndex,
            boundingBox,
            id,
            lockedContents: true,
            backgroundColor: Color.TRANSPARENT,
            fontSize: 12,
            customData: {
              type: 'INITIAL',
              color,
              name: signer.name,
              email: signer.email,
              signerId: signer.id,
              label: initials,
              status: isCurrentUser ? 'ACTIONED' : 'PENDING',
              signerType: signer.signerType,
              mobile: signer.mobile,
              autoAdded: true,
            },
          };

          let annotation;
          if (isCurrentUser) {
            annotation = new NutrientViewer.current.Annotations.TextAnnotation({
              ...annotationObj,
              text: {
                background: color,
                format: 'plain',
                fontSize: 12,
                value: initials,
              },
              horizontalAlign: 'center',
              verticalAlign: 'center',
            });
          } else {
            annotation = new NutrientViewer.current.Annotations.TextAnnotation({
              ...annotationObj,
              text: {
                format: 'plain',
                background: color,
                value: initials,
              },
              opacity: 0,
            });
          }

          await instanceRef.current.create(annotation);
        }

        if (onAnnotationUpdated) {
          onAnnotationUpdated();
        }
      },
      [instanceRef, userId, onAnnotationUpdated]
    );

    const removeInitialsFromAllPages = useCallback(
      async (signerId: string) => {
        if (!instanceRef.current) return;

        const totalPageCount = instanceRef.current.totalPageCount;
        const annotationsToDelete: string[] = [];

        for (let pageIndex = 0; pageIndex < totalPageCount; pageIndex++) {
          const pageAnnotations = await instanceRef.current.getAnnotations(
            pageIndex
          );
          pageAnnotations.forEach((annotation: any) => {
            if (
              annotation.customData?.type === 'INITIAL' &&
              annotation.customData?.signerId === signerId &&
              annotation.customData?.autoAdded
            ) {
              annotationsToDelete.push(annotation.id);
            }
          });
        }

        for (const id of annotationsToDelete) {
          await instanceRef.current.delete(id);
        }

        if (onAnnotationUpdated) {
          onAnnotationUpdated();
        }
      },
      [instanceRef, onAnnotationUpdated]
    );

    React.useEffect(() => {
      if (instanceRef?.current) {
        instanceRef.current.addInitialsToAllPages = addInitialsToAllPages;
        instanceRef.current.removeInitialsFromAllPages =
          removeInitialsFromAllPages;
      }
    }, [addInitialsToAllPages, removeInitialsFromAllPages]);

    const hanldeDeleteOK = async () => {
      await instanceRef.current.delete(showDeleteDlg?.id);
      onAnnotationUpdated && onAnnotationUpdated();
    };

    const signatureAddHandler = (signatureKey?: string | Blob) => {
      if (signatureKey && typeof signatureKey === 'string') {
        addSignatureToPdf(signatureKey, showAddSignModal);
      }
      setShowAddSignModal(null);
    };

    // This div element will render the document to the DOM.
    return (
      <>
        <WBFlex
          flex={1}
          ref={containerRef}
          height={'100%'}
          onDrop={handleDrop}
          onDragOver={(ev) => {
            ev.preventDefault();
          }}
        />
        {!!showDeleteDlg && (
          <ConfirmationDlg
            open={!!showDeleteDlg}
            onClose={() => setShowDeleteDlg(null)}
            onOK={hanldeDeleteOK}
            title={t('deleteConfirmationTitle', { ns: 'taskbox' })}
          >
            <WBTypography>
              {t('deleteConfirmationDescription', { ns: 'taskbox' })}
            </WBTypography>
          </ConfirmationDlg>
        )}
        {showAddSignModal && (
          <AddSignatureModal
            open={showAddSignModal}
            handleClose={() => setShowAddSignModal(null)}
            handleSave={signatureAddHandler}
          />
        )}
      </>
    );
  }
);
export default SignatureDropArea;
